import { Toaster as Sonner, type ToastClassnames } from 'sonner'

type ToasterProps = React.ComponentProps<typeof Sonner>

/**
 * The toast classes, shared by the live toaster and the `toaster` specimen.
 *
 * - Sonner injects its stylesheet unlayered, which beats Tailwind's
 *   `@layer utilities`, so every property Sonner also sets carries `!`.
 * - Sonner joins `toast` and the per-type classes without merging them, so
 *   each tone keys on `data-type` to outrank the glass fill.
 * - `richColors` must stay off, or Sonner's own tone palette takes over.
 *
 * The fill and tones copy `glassSurfaceClassName` and Alert's `toneSurfaces`
 * as literals, since Tailwind only sees literal class names;
 * `specimens.test.tsx` holds them in step.
 */
export const toastClassNames = {
	toast:
		'group toast bg-surface-glass! border-surface-glass-border! supports-[not(backdrop-filter:blur(0))]:bg-card! text-foreground! rounded-2xl! shadow-brand! border backdrop-blur-xl',
	title: 'text-(--alert-title)!',
	description:
		'text-muted-foreground! group-data-[type=error]:text-error-text!',
	actionButton:
		'bg-gradient-brand! text-brand-foreground! rounded-xl! font-medium',
	cancelButton: 'bg-muted! text-muted-foreground! rounded-xl!',
	info: 'data-[type=info]:border-info-border! data-[type=info]:bg-info-surface! [--alert-title:var(--color-info-text)]',
	success:
		'data-[type=success]:border-success-border! data-[type=success]:bg-success-surface! [--alert-title:var(--color-success-text)]',
	warning:
		'data-[type=warning]:border-destructive/35! data-[type=warning]:bg-destructive/7!',
	error:
		'data-[type=error]:border-destructive/55! data-[type=error]:bg-destructive/12! data-[type=error]:text-error-text!',
} satisfies ToastClassnames

const toastOptions = { classNames: toastClassNames }

const EpicToaster = ({ theme, ...props }: ToasterProps) => {
	return (
		<Sonner
			theme={theme}
			className="toaster group"
			toastOptions={toastOptions}
			{...props}
		/>
	)
}

export { EpicToaster }
